import React, { useState, useEffect } from "react";
import MHeader from "@/components/ui/organisms/MHeader";
import { getMemberInfo } from "@/hooks/useMembers";
import { useMemberPermission } from "@/hooks/useMemberPermission";
import { useLocation, useNavigate } from "react-router-dom";
import { useFamily } from "@/hooks/useFamily";
import { useMembers } from "@/hooks/useMembers";
import { relationshipOptions } from "@/types/relationship";
import { ListItem } from "@/components/ui/atoms/MListItem";
import MCheckbox from "@/components/ui/atoms/MCheckbox";
import { MButton } from "@/components/ui/atoms/MButtons";
import MemberCard from "../MemberCard";
import MAvatar from "@/components/ui/atoms/MAvatar";
import { IconUserStar, IconInfoCircle } from "@tabler/icons-react";
import MLoader from "@/components/ui/atoms/MLoader";
import memberPermissionService from "@/services/member-permissions.service";
import { isChild } from "@/types/member";
import MSpacer from "@/components/ui/atoms/MSpacer";
import MFrame from "@/components/ui/atoms/MFrame";
import { IconUserX } from "@tabler/icons-react";
import FooterDrawer from "@/components/ui/atoms/FooterDrawer";
import { useToast } from "@/components/ui/atoms/MToastProvider";
import { trackUserDataLoaded } from "@/utils/gtm";
import {
  ANALYTICS_SCREEN_TYPES,
  ANALYTICS_SCREEN_NAMES,
} from "@/constants/analytics.constants";

// Helper to get relationship label
function getRelationshipLabel(relationship: string | number | undefined) {
  if (typeof relationship === "number") {
    const rel = relationshipOptions.find((opt) => opt.value === relationship);
    return rel ? rel.label : "";
  } else if (typeof relationship === "string") {
    return relationship;
  }
  return "";
}

const AccessRequest: React.FC = () => {
  const memberInfo = getMemberInfo(true);
  const toast = useToast();
  const location = useLocation();
  const navigate = useNavigate();
  // Expecting selectedMember to be passed via location.state
  const selectedMember = location.state?.selectedMember;
  const { data: familyMembers = [] } = useFamily();
  const [localAccess, setLocalAccess] = useState<
    Array<{
      member: any;
      hasAccess: boolean;
      consentPending?: boolean;
      checkedAccess?: boolean;
    }>
  >([]);
  const [tokens, setTokens] = useState<any[]>([]);
  const [consentDrawerOpen, setConsentDrawerOpen] = useState(false);
  const [consentPendingInfoDrawerOpen, setConsentPendingInfoDrawerOpen] =
    useState(false);

  // Track GTM user_data_loaded event on page load
  useEffect(() => {
    if (!memberInfo?.id) return;

    trackUserDataLoaded({
      memberInfo,
      screenType: ANALYTICS_SCREEN_TYPES.FAMILY,
      screenName: ANALYTICS_SCREEN_NAMES.FAMILY_OWNER_MANAGE,
    });
  }, [memberInfo?.id]);

  useEffect(() => {
    async function fetchAccessList() {
      if (!selectedMember || familyMembers.length === 0) return;

      const { ids, tokens } =
        await memberPermissionService.getManagedMemberIdsAndTokens(
          selectedMember.id
        );
      setTokens(tokens || []);

      setLocalAccess(
        familyMembers
          .filter((m) => m.id !== selectedMember.id && m.registered_member)
          .map((m) => {
            const consentPending = tokens?.some((t) => t.member_id === m.id);
            const hasAccess =
              ids?.includes(m.id) || m.family_member_type === "fo";

            return {
              member: m,
              hasAccess,
              consentPending,
              checkedAccess: hasAccess || consentPending,
            };
          })
      );
    }

    fetchAccessList();
  }, [selectedMember?.id, familyMembers]);

  // Member permission hook for backend sync
  const { handleGrantAccess, handleRevokeAccess, loading } =
    useMemberPermission(selectedMember.id, selectedMember.current_family);

  // Handler for toggling checkboxes (UI only, no backend call)
  const handleToggle = (memberId: string) => {
    setLocalAccess((prev) =>
      prev.map((item) =>
        item.member.id === memberId
          ? { ...item, checkedAccess: !item.checkedAccess }
          : item
      )
    );
  };

  // Confirmation checkbox state
  const [confirmChecked, setConfirmChecked] = useState(false);

  // Handler for Done button
  const [submitting, setSubmitting] = useState(false);
  const handleDone = async () => {
    setSubmitting(true);
    const skipConsent = selectedMember?.member_type === "p";
    const revokePromises = localAccess
      .filter((item) => {
        const consentPending = tokens.some(
          (t) => t.member_id === item.member.id
        );
        const wasChecked = item.hasAccess || consentPending;
        const nowChecked = !!item.checkedAccess;
        return (
          wasChecked && !nowChecked && item.member.family_member_type !== "fo"
        );
      })
      .map((item) => {
        const consentPending = tokens.some(
          (t) => t.member_id === item.member.id
        );
        if (consentPending) {
          // Find the token for this member
          const tokenObj = tokens.find((t) => t.member_id === item.member.id);
          return memberPermissionService.revokeInvite({
            memberId: item.member.id,
            familyId: selectedMember.current_family,
            familyMemberId: selectedMember.id,
            token: tokenObj?.token,
          });
        } else if (item.hasAccess) {
          return memberPermissionService.revokeAccess({
            memberId: item.member.id,
            familyId: selectedMember.current_family,
            familyMemberId: selectedMember.id,
          });
        }
        return Promise.resolve();
      });
    const grantPromises = localAccess
      .filter((item) => {
        const consentPending = tokens.some(
          (t) => t.member_id === item.member.id
        );
        const wasChecked = item.hasAccess || consentPending;
        const nowChecked = !!item.checkedAccess;
        return (
          !wasChecked && nowChecked && item.member.family_member_type !== "fo"
        );
      })
      .map((item) =>
        handleGrantAccess(selectedMember.id, item.member.id, skipConsent)
      );
    const hasInvites = !skipConsent && grantPromises.length > 0;
    const hasRevokes = revokePromises.length > 0;
    try {
      await Promise.all([...revokePromises, ...grantPromises]);
      if (hasInvites) {
        toast.showToast({
          message: "Access grant notification sent",
          variant: "default",
        });
      } else if (hasRevokes) {
        toast.showToast({
          message: "Access removal notification sent",
          variant: "default",
        });
      } else {
        toast.showToast({
          message: "Access grant notification sent",
          variant: "default",
        });
      }
      navigate(-1);
    } catch {
      toast.showToast({ message: "Please try again.", variant: "error" });
    } finally {
      setSubmitting(false);
    }
  };
  const hasChanges = localAccess.some((item) => {
    if (item.member.family_member_type === "fo") return false;
    const consentPending = item.consentPending;
    const wasChecked = item.hasAccess || consentPending;
    const nowChecked = item.checkedAccess;
    return wasChecked !== nowChecked;
  });

  if (
    loading ||
    submitting ||
    (localAccess.length === 0 &&
      !(
        selectedMember.family_member_type === "fo" &&
        familyMembers.filter((m: any) => m.registered_member === true)
          .length === 1
      ))
  ) {
    return <MLoader />;
  }

  return (
    <>
      <div className="profile-page-root min-h-screen flex flex-col">
        <MHeader
          module="Family"
          memberInfo={memberInfo}
          title={
            selectedMember.id === memberInfo.id
              ? "Who can access your profile"
              : `Who can access ${
                  selectedMember.nickname || selectedMember.first_name
                }'s profile`
          }
          description={
            selectedMember.id === memberInfo.id
              ? "Choose who in your family with a Maatra account can access your profile."
              : selectedMember.member_type === "h" &&
                isChild({
                  dob_year: selectedMember.dob_year,
                  dob_month: selectedMember.dob_month,
                  dob_day: selectedMember.dob_day,
                })
              ? "You can choose a parent or legal guardian to provide access to a child's profile."
              : memberInfo.family_member_type === "fm" &&
                selectedMember.id !== memberInfo.id
              ? `View who in your family with a Maatra account can access ${
                  selectedMember.nickname || selectedMember.first_name
                }'s profile.`
              : "Choose who in your family with a Maatra account can access this member's profile."
          }
          learnMoreContent={
            selectedMember.id === memberInfo.id ? (
              <div>
                You can decide which family members see your health information.
                Only members who have a Maatra account can be given access.
                <br />
                <br />
                Granting access allows them to view or update your health
                records.
              </div>
            ) : selectedMember.member_type === "h" &&
              isChild({
                dob_year: selectedMember.dob_year,
                dob_month: selectedMember.dob_month,
                dob_day: selectedMember.dob_day,
              }) ? (
              <div>
                It is a legal requirement in India that only parents or legal
                guardians be given access to a child's health records.
                <br />
                <br />
                Granting access allows the selected members to view, add, edit,
                and delete the child's health records in Maatra.
              </div>
            ) : memberInfo.family_member_type === "fm" &&
              selectedMember.id !== memberInfo.id ? (
              <div>
                This shows the family member's who can currently access this
                profile.
                <br />
                <br />
                If you'd like to change access, you can ask the Family Owner.
              </div>
            ) : memberInfo.family_member_type === "fo" &&
              selectedMember.family_member_type === "fm" ? (
              <div>
                As a Family Owner, you decide which family members see another
                member's health information. Only members who have a Maatra
                account can be given access.
                <br />
                <br />
                Giving access allows them to view or update health records.
              </div>
            ) : (
              "Granting access allows the selected members to view, add, edit, and delete health records in Maatra. Only registered family members can be given access."
            )
          }
          hideBack={true}
        />
        <MSpacer mt={32} />
        <div className="flex flex-col gap-3">
          {/* Show 'No one has access yet' message if selectedMember is FO and is the only registered member */}
          {selectedMember.family_member_type === "fo" &&
            familyMembers.filter((m: any) => m.registered_member === true)
              .length === 1 && (
              <MFrame>
                <div className="mframeInner">
                  <IconUserX
                    size={64}
                    color="#AD95D1"
                    style={{ marginBottom: 15 }}
                  />
                  <div
                    className="body-base-semibold color-text-indigo-600"
                    style={{ marginBottom: 15 }}
                  >
                    No one has access yet
                  </div>
                  <div className="accessRequestEmptyText">
                    No family members have a Maatra account yet. Invite a family
                    member to join Maatra and share access.
                  </div>
                </div>
              </MFrame>
            )}
          {[...localAccess]
            .filter(
              (item) =>
                item.member.id !== selectedMember.id &&
                item.member.registered_member === true
            )
            .sort((a, b) => {
              if (a.member.family_member_type === "fo") return -1;
              if (b.member.family_member_type === "fo") return 1;
              return 0;
            })
            .map((item) => {
              const consentPending = item.consentPending;
              let accessStatus: string | undefined = undefined;
              let accessStatusClass = "";

              if (consentPending) {
                accessStatus = (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                    }}
                  >
                    <span>Consent Pending</span>
                    <IconInfoCircle
                      size={16}
                      color="#407ED0"
                      style={{ cursor: "pointer" }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setConsentPendingInfoDrawerOpen(true);
                      }}
                    />
                  </div>
                ) as any;
                accessStatusClass = "";
              } else if (item.hasAccess) {
                accessStatus = "Access Provided";
                accessStatusClass = "statusLabelRegistered";
              } else {
                accessStatus = "No Access";
                accessStatusClass = "accessStatusNoAccess";
              }

              return (
                <MemberCard
                  key={item.member.id}
                  name={`${
                    item.member.nickname || item.member.first_name
                  }`.trim()}
                  isOwner={item.member.family_member_type === "fo"}
                  relationship={getRelationshipLabel(item.member.relationship)}
                  member_type={item.member.family_member_type}
                  className="cursor-pointer"
                  menu={
                    <MCheckbox
                      checked={item.checkedAccess}
                      disabled={
                        item.member.family_member_type === "fo" ||
                        loading ||
                        (memberInfo.family_member_type === "fm" &&
                          selectedMember.id !== memberInfo.id)
                      }
                      onChange={() => handleToggle(item.member.id)}
                    />
                  }
                  registered={item.member.registered_member}
                  invited={item.member.invited}
                  accessStatus={accessStatus}
                  accessStatusClass={accessStatusClass}
                  showStatusLabels={true}
                />
              );
            })}
        </div>
        {selectedMember.member_type === "h" &&
          isChild({
            dob_year: selectedMember.dob_year,
            dob_month: selectedMember.dob_month,
            dob_day: selectedMember.dob_day,
          }) &&
          familyMembers.filter((m) => m.registered_member).length > 1 && (
            <div className="flex flex-row items-center gap-2 mt-8 mb-6 px-2">
              <MCheckbox
                checked={confirmChecked}
                onChange={() => setConfirmChecked((v) => !v)}
                id="confirm-checkbox"
              />
              <label
                htmlFor="confirm-checkbox"
                className="body-base-medium default-color"
                style={{ cursor: "pointer", marginLeft: 16 }}
              >
                I confirm that the selected adults are the child’s parents or
                legal guardians.
              </label>
            </div>
          )}
        {familyMembers.filter((m) => m.registered_member).length > 1 && (
          <div className="confirm-footer">
            <MButton
              size="lg"
              className="w-full"
              onClick={handleDone}
              disabled={
                submitting ||
                !hasChanges ||
                (selectedMember.member_type === "h" &&
                  isChild({
                    dob_year: selectedMember.dob_year,
                    dob_month: selectedMember.dob_month,
                    dob_day: selectedMember.dob_day,
                  }) &&
                  !confirmChecked)
              }
            >
              Done
            </MButton>
          </div>
        )}
        <FooterDrawer
          open={consentDrawerOpen}
          onClose={() => setConsentDrawerOpen(false)}
          title="Details"
          showClose
        >
          <div className="accessRequestText">
            The Family Owner has invited this member to access your profile. A
            consent link, valid for 7 days, has been sent to them.
            <br />
            <br />
            Since the member hasn’t responded yet, their status appears as
            ’Consent pending’. Once they respond, you’ll be able to add or
            remove their access.
          </div>
        </FooterDrawer>

        <FooterDrawer
          open={consentPendingInfoDrawerOpen}
          onClose={() => {
            setConsentPendingInfoDrawerOpen(false);
          }}
          title="Consent Pending"
        >
          <div className="accessRequestText">
            {memberInfo.family_member_type === "fo" &&
            selectedMember.family_member_type === "fo" ? (
              <>
                Notification sent to this family member to access your profile.
                <br />
                <br />
                They'll be able to access the profile once they give consent.
              </>
            ) : (
              <>
                Notification sent to this family member to access{" "}
                {selectedMember?.nickname || selectedMember?.first_name}'s
                profile.
                <br />
                <br />
                They'll be able to access the profile once they give consent.
              </>
            )}
          </div>
        </FooterDrawer>
      </div>
    </>
  );
};

export default AccessRequest;
